import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Paper,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import API_URL from '../../config/api'
import './chatdialog.scss'

// Función para generar UUID simple
const generateSessionId = () => {
  return 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
}

const SESSION_KEY = 'chat_session_id'
const SESSION_TS_KEY = 'chat_session_created_at'
const SESSION_TTL_MS = 12 * 60 * 60 * 1000 // 12 horas

const getStoredSession = () => {
  const storedId = sessionStorage.getItem(SESSION_KEY)
  const storedTs = sessionStorage.getItem(SESSION_TS_KEY)
  if (!storedId || !storedTs) return null

  const createdAt = Number(storedTs)
  if (Number.isNaN(createdAt)) return null

  const expired = Date.now() - createdAt > SESSION_TTL_MS
  if (expired) return null

  return { id: storedId, createdAt }
}

const storeSession = (id) => {
  sessionStorage.setItem(SESSION_KEY, id)
  sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()))
}

export default function ChatDialog({ open, onClose }) {
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [sessionId, setSessionId] = useState('')
  const messagesEndRef = useRef(null)

  // Inicializar sesión
  useEffect(() => {
    if (!open || sessionId) return

    const stored = getStoredSession()
    if (stored) {
      setSessionId(stored.id)
      if (messages.length === 0) {
        setMessages([
          {
            id: 1,
            sender: 'bot',
            content: '¡Hola! Soy el asistente de Ingeniosoft. ¿En qué puedo ayudarte?',
          },
        ])
      }
      return
    }

    const newSessionId = generateSessionId()
    storeSession(newSessionId)
    setSessionId(newSessionId)

    // Mensaje de bienvenida
    setMessages([
      {
        id: 1,
        sender: 'bot',
        content: '¡Hola! Soy el asistente de Ingeniosoft. ¿En qué puedo ayudarte?',
      },
    ])
  }, [open, sessionId, messages.length])

  // Auto-scroll a los últimos mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Función para simular streaming del texto (efecto de escritura)
  const typeMessage = async (fullContent, messageId) => {
    const chars = fullContent.split('')
    let currentContent = ''
    
    for (let i = 0; i < chars.length; i++) {
      currentContent += chars[i]
      
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, content: currentContent } : msg
        )
      )
      
      // Velocidad de escritura: 30ms por carácter (ajustable)
      await new Promise((resolve) => setTimeout(resolve, 30))
    }
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!inputValue.trim() || loading) return

    // Agregar mensaje del usuario
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      content: inputValue,
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setLoading(true)

    try {
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: inputValue,
        }),
        signal: AbortSignal.timeout(150000), // 2.5 minutos de timeout
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Error al enviar el mensaje')
      }

      // Agregar respuesta del bot con contenido vacío inicialmente
      const botMessageId = Date.now() + 1
      const botMessage = {
        id: botMessageId,
        sender: 'bot',
        content: '', // Iniciar vacío
      }

      setMessages((prev) => [...prev, botMessage])
      
      // Simular streaming del texto
      await typeMessage(data.response, botMessageId)
    } catch (err) {
      console.error('Error:', err)
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        content: err.name === 'AbortError' 
          ? 'La respuesta está tardando más de lo esperado. Por favor intenta de nuevo.' 
          : 'Disculpa, hubo un error. Por favor intenta de nuevo.',
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setInputValue('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="chat-dialog"
      PaperProps={{
        sx: {
          height: '600px',
          m: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Asistente Ingeniosoft 360
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'white' }}
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {messages.map((msg) => (
          <Paper
            key={msg.id}
            className={`chat-message chat-message--${msg.sender} ${
              msg.sender === 'bot' && loading && msg.id === messages[messages.length - 1]?.id ? 'typing' : ''
            }`}
            sx={{
              p: 1.5,
              maxWidth: '85%',
              alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              backgroundColor: msg.sender === 'user' ? '#667eea' : '#f0f4f6',
              color: msg.sender === 'user' ? 'white' : '#0f172a',
              borderRadius: 2,
            }}
            elevation={0}
          >
            <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
              {msg.content}
            </Typography>
          </Paper>
        ))}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 1 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <div ref={messagesEndRef} />
      </DialogContent>

      <DialogActions
        sx={{
          padding: 2,
          gap: 1,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Box
          component="form"
          onSubmit={sendMessage}
          sx={{
            width: '100%',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Escribe tu mensaje..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                sendMessage(e)
              }
            }}
            disabled={loading}
            multiline
            maxRows={3}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Button
            type="submit"
            disabled={loading || !inputValue.trim()}
            variant="contained"
            sx={{
              background: 'linear-gradient(90deg, #8711e2 0%, #00BFA6 100%)',
              minWidth: '40px',
              p: 1,
              // color: 'white',
            }}
            aria-label="Enviar mensaje"
          >
            <SendIcon />
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
